import { INTENTS, FALLBACK } from '@/data/chatKnowledge';

/** Words too common to carry intent signal on their own. */
const STOP_WORDS = new Set([
  'a', 'an', 'the', 'is', 'are', 'am', 'was', 'were', 'be', 'been', 'do', 'does', 'did', 'can', 'could',
  'will', 'would', 'should', 'shall', 'may', 'might', 'must', 'i', 'me', 'my', 'we', 'our', 'you', 'your',
  'it', 'its', 'this', 'that', 'these', 'those', 'and', 'or', 'but', 'if', 'to', 'of', 'in', 'on', 'for',
  'with', 'about', 'from', 'at', 'by', 'as', 'so', 'then', 'there', 'here', 'what', 'which', 'who', 'whom',
  'how', 'when', 'where', 'why', 'not', 'no', 'yes', 'please', 'tell', 'know', 'want', 'need', 'get', 'have',
]);

/** Lowercase, strip punctuation, collapse whitespace. */
export function normalize(input) {
  return String(input || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenize(text) {
  return normalize(text)
    .split(' ')
    .filter((w) => w.length > 1 && !STOP_WORDS.has(w));
}

/**
 * A phrase match is strong evidence; individual keywords are weaker and capped so a long
 * rambling message cannot out-score a precise one.
 */
function scoreIntent(intent, normalized, tokens) {
  let score = 0;
  for (const phrase of intent.phrases || []) {
    if (normalized.includes(normalize(phrase))) score += 6;
  }
  let keywordHits = 0;
  for (const keyword of intent.keywords || []) {
    if (tokens.includes(normalize(keyword))) keywordHits += 1;
  }
  score += Math.min(keywordHits, 3) * 2;

  /* Short messages ("hello", "thanks", "pricing") carry all their meaning in one or two words,
     so keyword coverage matters more than raw hit count. Kept below the phrase weight so an
     explicit phrase match still wins — "is it free" is a pricing question, not a demo one. */
  if (keywordHits > 0 && tokens.length <= 3 && keywordHits / tokens.length >= 0.5) {
    score += 3;
  }
  return score;
}

/** Intents scoped to the other audience still match, but rank below on-audience ones. */
function audienceBonus(intent, audience) {
  if (intent.audience === 'both') return 0.5;
  return intent.audience === audience ? 2 : -1.5;
}

const MATCH_THRESHOLD = 4;

/**
 * Resolve a free-text message to a canned answer.
 * @param {string} message
 * @param {'student'|'school'} audience
 * @returns {{ matched: boolean, intentId: string, text: string, chips: string[], actions: string[], escalate: boolean }}
 */
export function matchIntent(message, audience = 'student') {
  const normalized = normalize(message);
  const tokens = tokenize(message);

  if (!normalized) {
    return { matched: false, intentId: 'empty', text: FALLBACK.text, chips: FALLBACK.chips, actions: [], escalate: false };
  }

  let best = null;
  let bestScore = 0;
  for (const intent of INTENTS) {
    const base = scoreIntent(intent, normalized, tokens);
    if (base <= 0) continue;
    const total = base + audienceBonus(intent, audience);
    if (total > bestScore) {
      bestScore = total;
      best = intent;
    }
  }

  if (!best || bestScore < MATCH_THRESHOLD) {
    return {
      matched: false,
      intentId: 'fallback',
      text: FALLBACK.text,
      chips: FALLBACK.chips,
      actions: [],
      escalate: false,
    };
  }

  return {
    matched: true,
    intentId: best.id,
    text: best.answer,
    /* Resolved by the widget against live server data (e.g. current plan prices). */
    dynamic: best.dynamic || null,
    chips: best.chips || [],
    actions: best.actions || [],
    escalate: Boolean(best.escalate),
  };
}
